import React from 'react';
import { observer } from 'mobx-react';
import { computed , observable } from 'mobx';
import styled from '@emotion/styled';
import Text from 'components/text';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import { each, intersection, isFunction, startCase } from 'lodash';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: ${props => props.width};
  height: fit-content;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
`;

const ScrollWrapper = styled.div`
  display: flex;
  height: fit-content;
  flex-wrap: ${props => props.shouldWrap ? 'wrap' : 'nowrap'};
  align-items: center;
  min-width: 0;
  max-width: ${props => props.shouldWrap ? 'none' : '650px'};
  overflow-x: ${props => props.shouldWrap ? 'visible' : 'auto'};
  scrollbar-width: ${props => props.shouldWrap ? 'auto' : 'none'};
  position: relative;
  mask-image: ${props => {
    if (props.shouldWrap || !props.hasOverflow) return 'none';

    const hasLeft = props.hasScrolledFromStart;
    const hasRight = !props.hasScrolledToEnd;

    if (hasLeft && hasRight) {
      return 'linear-gradient(to right, transparent 0%, black 20px, black calc(100% - 20px), transparent 100%)';
    } else if (hasLeft && !hasRight) {
      return 'linear-gradient(to right, transparent 0%, black 20px, black 100%)';
    } else if (!hasLeft && hasRight) {
      return 'linear-gradient(to right, black 0%, black calc(100% - 20px), transparent 100%)';
    } else {
      return 'none';
    }
  }};
  -webkit-mask-image: ${props => {
    if (props.shouldWrap || !props.hasOverflow) return 'none';

    const hasLeft = props.hasScrolledFromStart;
    const hasRight = !props.hasScrolledToEnd;

    if (hasLeft && hasRight) {
      return 'linear-gradient(to right, transparent 0%, black 20px, black calc(100% - 20px), transparent 100%)';
    } else if (hasLeft && !hasRight) {
      return 'linear-gradient(to right, transparent 0%, black 20px, black 100%)';
    } else if (!hasLeft && hasRight) {
      return 'linear-gradient(to right, black 0%, black calc(100% - 20px), transparent 100%)';
    } else {
      return 'none';
    }
  }};
  padding: 6px 0;
  gap: ${props => props.shouldWrap ? '8px' : '0'};

  &::-webkit-scrollbar {
    display: ${props => props.shouldWrap ? 'block' : 'none'};
  }
`;

const Tag = styled.div`
  background: #FFF;
  padding: 8px 16px;
  padding-right: ${props => props.paddingRight};
  border-radius: 22px;
  position: relative;
  border: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  min-width: 0;
  max-width: 100%;
  height: 34px;
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin-right: ${props => props.shouldWrap ? '0' : '3px'};
  min-width: fit-content;
`;

const TimesIconWrapper = styled.div`
  cursor: pointer;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;

  i {
    font-size: 12px;
    line-height: 1;
  }
`;

@observer
class WorksheetFilterTags extends React.Component {
  @observable hasOverflow = false;
  @observable hasScrolledFromStart = false;
  @observable hasScrolledToEnd = false;
  scrollWrapperRef = React.createRef();

  componentDidMount() {
    if (!this.props.wrap) {
      this.checkOverflow();
      this.checkScrollPosition();
      window.addEventListener('resize', this.checkOverflow);
      if (this.scrollWrapperRef.current) {
        this.scrollWrapperRef.current.addEventListener('scroll', this.checkScrollPosition);
      }
    }
  }

  componentDidUpdate() {
    if (!this.props.wrap) {
      this.checkOverflow();
      this.checkScrollPosition();
    }
  }

  componentWillUnmount() {
    if (!this.props.wrap) {
      window.removeEventListener('resize', this.checkOverflow);
      if (this.scrollWrapperRef.current) {
        this.scrollWrapperRef.current.removeEventListener('scroll', this.checkScrollPosition);
      }
    }
  }
  checkOverflow = () => {
    if (this.scrollWrapperRef.current) {
      const element = this.scrollWrapperRef.current;
      this.hasOverflow = element.scrollWidth > element.clientWidth;
    }
  }
  checkScrollPosition = () => {
    if (this.scrollWrapperRef.current) {
      const element = this.scrollWrapperRef.current;
      this.hasScrolledFromStart = element.scrollLeft > 0;
      this.hasScrolledToEnd = element.scrollLeft >= (element.scrollWidth - element.clientWidth);
    }
  }
  onRemoveTag = (tagCategory) => {
    let ary = this.userTagIdsAry;
    each(tagCategory.tags, tag => {
      ary = ary.filter(item => item !== tag.id);
    });
    const stringValue = isPresent(ary) ? ary.join(',') : null;
    if (this.props.isExerciseFilter) {
      AppUI.cookieStore.set('exerciseTagIds', stringValue || null);
    } else {
      AppUI.cookieStore.set(`worksheetTagIds${this.props.activeView}`, stringValue || null);
    }
    if (isFunction(this.props.onUpdateFilter)) {
      this.props.onUpdateFilter();
    }
  }
  onRemoveProgressTag = () => {
    if (this.props.isExerciseFilter) {
      AppUI.cookieStore.set('exerciseProgressTags', null);
    } else {
      AppUI.cookieStore.set(`worksheetProgressTags${this.props.activeView}`, null);
    }
    if (isFunction(this.props.onUpdateFilter)) {
      this.props.onUpdateFilter();
    }
  }

  @computed get progressTagCategory() {
    return this.props.tagCategories.find(item => item.slug === 'progress');
  }

  @computed get userTagIds() {
    if (this.props.isExerciseFilter) {
      return AppUI.cookieStore.exerciseTagIds;
    } else {
      return AppUI.cookieStore[`worksheetTagIds${this.props.activeView}`];
    }
  }

  @computed get userTagIdsAry() {
    return this.userTagIds?.split(',') || [];
  }

  @computed get userProgressTags() {
    if (this.props.isExerciseFilter) {
      return AppUI.cookieStore.exerciseProgressTags;
    } else {
      return AppUI.cookieStore[`worksheetProgressTags${this.props.activeView}`];
    }
  }
  tagCategoryText = (tagCategory) => {
    const maxLength = 25;
    const text = tagCategory.tags.filter(item => this.userTagIdsAry.includes(item.id)).map(item => item.name).join(', ');
    if (text.length > maxLength) {
      return `${text.slice(0, maxLength).trim()}...`;
    }
    return text;
  }

  render() {
    return (
      <Wrapper
        width={this.props.width}
      >
        <ScrollWrapper
          ref={this.scrollWrapperRef}
          hasOverflow={this.hasOverflow}
          hasScrolledFromStart={this.hasScrolledFromStart}
          hasScrolledToEnd={this.hasScrolledToEnd}
          shouldWrap={this.props.wrap}
        >
          {
            isPresent(this.userProgressTags) && this.props.showProgressTag &&
              <Tag
                paddingRight='32px'
                index={0}
              >
                <Text
                  color='#333'
                  bold
                  fontSize={12}
                  lineHeight={15}
                  style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}
                >
                  {this.userProgressTags.split(',').map(item => isPresent(this.progressTagCategory.tags.find(tag => tag.slug === item)) ? this.progressTagCategory.tags.find(tag => tag.slug === item).name : startCase(item.replace('-', ' '))).join(', ')}
                </Text>
                <TimesIconWrapper
                  onClick={this.onRemoveProgressTag}
                >
                  <i className='fa fa-times' />
                </TimesIconWrapper>
              </Tag>
          }
          {
            this.props.tagCategories?.map((tagCategory, index) => {
              if (isPresent(intersection(this.userTagIdsAry, tagCategory.tags.map(item => item.id)))) {
                return (
                  <Tag
                    key={tagCategory.id}
                    paddingRight='32px'
                    index={(isPresent(this.userProgressTags) && this.props.showProgressTag) ? index + 1 : index}
                  >
                    <Text
                      color='#333'
                      bold
                      fontSize={12}
                      lineHeight={15}
                      style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}
                    >
                      {this.tagCategoryText(tagCategory)}
                    </Text>
                    <TimesIconWrapper
                      onClick={() => this.onRemoveTag(tagCategory)}
                    >
                      <i className='fa fa-times' />
                    </TimesIconWrapper>
                  </Tag>
                );
              } else {
                return null;
              }
            })
          }
        </ScrollWrapper>
      </Wrapper>
    );
  }
}

export default WorksheetFilterTags;
